/**
 * Addressable modals. A trigger link's first query param is the source of truth
 * for open state, so every modal is shareable, bookmarkable, and closes on Back.
 *
 * Prefer this over an imperative open handler: `syncModalsFromUrl` opens whichever
 * modal matches the URL on load / popstate, `openModal` writes the param, and
 * `closeModal` clears it. Nothing else needs to know a modal exists.
 *
 * @usage
 *   <a href="?invite=5" aria-controls="invite-modal-5" aria-haspopup="dialog">Invite</a>
 *   <dialog id="invite-modal-5" class="modal">…</dialog>
 *
 * To reopen a modal after a failed POST, render the response at that same
 * `?param=value` — point the form's action at it (`action="…?add-companion=1"`)
 * and `syncModalsFromUrl` reopens it on load, errors and all. No server-set flag.
 *
 * Triggers must be same-path query links (`?x=y`), not buttons: the Navigation API
 * interception below only fires for anchor navigations to the current pathname.
 */

import { restoreCarriedSearchParams } from "./url-state";

interface NavigateEvent {
  canIntercept: boolean;
  destination: { url: string };
  formData?: FormData | null;
  hashChange: boolean;
  intercept: (options?: {
    focusReset?: "after-transition" | "manual";
    handler?: () => Promise<void> | void;
    scroll?: "after-transition" | "manual";
  }) => void;
  navigationType: "push" | "reload" | "replace" | "traverse";
  sourceElement?: Element | null;
}

interface Navigation {
  addEventListener(type: "navigate", handler: (e: NavigateEvent) => void): void;
}

/** ~16% lack Navigation API (Firefox on Android, IE11, older Safari); there, every trigger falls back to a click handler. */
const { navigation } = globalThis as { navigation?: Navigation };

const openingModals = new Set<string>();

const getDialog = (id: string): HTMLDialogElement => {
  const element = document.getElementById(id);
  if (!(element instanceof HTMLDialogElement)) {
    throw new TypeError(`Modal "${id}" is not a <dialog> element`);
  }
  return element;
};

const updateQueryParam = (
  paramName: string,
  value: string | null,
  { replaceHistory = false } = {},
): void => {
  const url = new URL(globalThis.location.href);
  const current = url.searchParams.get(paramName);

  if (value === null) {
    if (!current) return;
    url.searchParams.delete(paramName);
  } else {
    const next = String(value);
    if (current === next) return;
    url.searchParams.set(paramName, next);
  }

  if (replaceHistory) {
    globalThis.history.replaceState({}, "", url);
    return;
  }
  globalThis.history.pushState({}, "", url);
};

const getLinkableByModalId = (id: string): { paramName: string; paramValue: string } | null => {
  const link = document.querySelector(`a[href][aria-controls="${id}"]`);
  if (!link) return null;

  const href = link.getAttribute("href");
  if (!href) return null;

  const hrefUrl = new URL(href, globalThis.location.href);
  const first = hrefUrl.searchParams.entries().next();
  if (first.done) return null;

  const [paramName, paramValue] = first.value;
  return { paramName, paramValue };
};

const prefersReducedMotion = (): boolean =>
  globalThis.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;

// iPadOS reports itself as a Mac, hence the touch-point check behind the UA
// match. navigator.platform is deprecated but still the one signal iPadOS
// leaves; when it goes, that branch quietly stops matching iPads and nothing
// else breaks.
const isAppleTouchDevice = (): boolean =>
  /iPhone|iPad|iPod/.test(navigator.userAgent) ||
  (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

// Whether a modal closes with no view transition at all. Apple touch devices
// do, in every layout: measured on the live 880-session schedule in WebKit,
// the transition's capture alone held every tap for about a second before the
// dialog even closed, where Chromium takes a frame — and a reader closing a
// session is usually reaching for the search box next. Only the close: the
// open morph runs on the same engine, but nobody is tapping the page while a
// modal opens, so its cost buys the animation without taking anything.
const closesWithoutTransition = (): boolean => prefersReducedMotion() || isAppleTouchDevice();

// Held on <html> for a transition's lifetime: it opts the root out of the
// capture (modal.css), which is what keeps the page under a modal animation
// tappable — the root's snapshot would otherwise stand in for every element on
// it, and a snapshot takes no taps.
const ROOT_PAGE_LIVE = "vt-page-live";

// NOTE: lib.dom declares startViewTransition on every Document; Firefox
// before 144 has none.
const hasViewTransitions = (): boolean => typeof document.startViewTransition === "function";

const startViewTransition = (callback: () => void): ViewTransition | null => {
  if (!hasViewTransitions()) {
    callback();
    return null;
  }
  document.documentElement.classList.add(ROOT_PAGE_LIVE);
  const transition = document.startViewTransition(callback);
  // NOTE: a skipped or aborted transition rejects `ready`, and Chrome 151 no
  // longer marks that rejection handled, so leaving it unawaited is an error.
  void transition.ready.catch(() => null);
  void transition.finished
    .catch(() => null)
    .finally(() => document.documentElement.classList.remove(ROOT_PAGE_LIVE));
  return transition;
};

const isSkippedTransitionError = (error: unknown): boolean =>
  error instanceof DOMException &&
  error.name === "AbortError" &&
  error.message.includes("Transition was skipped");

const ignoreSkippedTransition = (error: unknown): void => {
  if (isSkippedTransitionError(error)) return;
  throw error;
};

const MORPH_NAME = "session-morph";
const CARD_SUPPRESSED = "session-suppressed";

const sessionCardForModal = (id: string): HTMLElement | null => {
  if (!id.startsWith("session-")) return null;
  const card = document.querySelector(
    `.session[data-session-id="${CSS.escape(id.slice("session-".length))}"]`,
  );
  return card instanceof HTMLElement ? card : null;
};

const suppressSessionCard = (id: string): void => {
  sessionCardForModal(id)?.classList.add(CARD_SUPPRESSED);
};

const releaseSessionCard = (id: string): void => {
  sessionCardForModal(id)?.classList.remove(CARD_SUPPRESSED);
};

const canMorph = (card: HTMLElement | null): card is HTMLElement =>
  card !== null &&
  card.dataset.noMorph === undefined &&
  !prefersReducedMotion() &&
  hasViewTransitions();

const setSubMorph = (root: HTMLElement, active: boolean): void => {
  for (const el of root.querySelectorAll<HTMLElement>("[data-morph]")) {
    el.style.viewTransitionName = active ? `morph-${el.dataset.morph}` : "";
  }
};

const setContainerMorph = (root: HTMLElement, active: boolean): void => {
  root.style.viewTransitionName = active ? MORPH_NAME : "";
};

const setMorph = (root: HTMLElement, active: boolean): void => {
  setContainerMorph(root, active);
  setSubMorph(root, active);
};

const morphTransition = (steps: {
  before: () => void;
  settle: () => void;
  swap: () => void;
}): Promise<void> => {
  steps.before();
  const transition = startViewTransition(steps.swap);
  if (!transition) {
    steps.settle();
    return Promise.resolve();
  }
  return transition.finished.catch(ignoreSkippedTransition).finally(steps.settle);
};

// The plain exit: the dialog's own snapshot animates out (modal.css). Whether
// to animate at all is the caller's call, decided once in closeModal.
const dismissDialog = (dialog: HTMLDialogElement): void => {
  startViewTransition(() => {
    dialog.close();
  })?.finished.catch((error) => {
    try {
      ignoreSkippedTransition(error);
    } catch (error_) {
      console.error(error_);
    }
  });
};

/**
 * Fetched dialogs are cached in the DOM, so a reopen inherits wherever the last
 * visit left the panel scrolled. That is worse than a cosmetic glitch here: the
 * morph captures the modal's new state at open, so a stale offset is baked into
 * the animation's destination and the card appears to expand into a modal that
 * is already scrolled.
 *
 * Reset on open, not on close, because opening has one choke point and closing
 * has none — `closeModal` alone branches twice, `dismissDialog` branches again,
 * and Esc or a `form[method=dialog]` closes the dialog without reaching either.
 * `session-edit.ts` already shows what that costs: it binds its `close` listener
 * over `document.querySelectorAll` at parse time, so the lazily fetched dialogs
 * this function exists for never run it.
 *
 * A closed dialog is also `display: none`, and `scrollTop` is a no-op on an
 * element with no layout box, so a reset after `close()` would silently do
 * nothing. Same rule puts this after `showModal()` rather than before it.
 */
const resetModalScroll = (dialog: HTMLDialogElement): void => {
  // Not a class selector: the tab panels are the only scrollers in the rendered
  // markup, but htmx swaps the edit form's own `overflow-y-auto` body in at
  // runtime, and a dialog closed mid-edit keeps it (see above).
  for (const element of dialog.querySelectorAll<HTMLElement>("*")) {
    if (element.scrollTop !== 0) element.scrollTop = 0;
  }
};

// Both open paths need `showModal()` and the reset in that order, and the order
// is load-bearing, so it lives here rather than in a comment at each call site.
const presentDialog = (dialog: HTMLDialogElement): void => {
  dialog.showModal();
  resetModalScroll(dialog);
};

const openModal = async (
  id: string,
  { animate = true, replaceHistory = false, updateUrl = true } = {},
): Promise<void> => {
  const dialog = getDialog(id);
  let morphPromise: Promise<void> | null = null;
  if (!dialog.open && !openingModals.has(id)) {
    const card = sessionCardForModal(id);
    if (animate && canMorph(card)) {
      openingModals.add(id);
      morphPromise = morphTransition({
        before: () => {
          setMorph(card, true);
        },
        settle: () => {
          openingModals.delete(id);
          setMorph(dialog, false);
          card.style.transition = "";
        },
        swap: () => {
          setMorph(card, false);
          // NOTE: a DOM swap can detach the dialog while the transition is queued.
          if (!dialog.isConnected) {
            const replacement = document.getElementById(id);
            if (!(replacement instanceof HTMLDialogElement && replacement.open)) {
              releaseSessionCard(id);
              const linkable = getLinkableByModalId(id);
              if (
                linkable &&
                new URLSearchParams(globalThis.location.search).get(linkable.paramName) ===
                  linkable.paramValue
              ) {
                updateQueryParam(linkable.paramName, null, { replaceHistory: true });
              }
            }
            return;
          }
          suppressSessionCard(id);
          card.style.transition = "none";
          presentDialog(dialog);
          setMorph(dialog, true);
        },
      });
    } else {
      presentDialog(dialog);
      suppressSessionCard(id);
    }
  }

  if (updateUrl) {
    const linkable = getLinkableByModalId(id);
    if (linkable) {
      updateQueryParam(linkable.paramName, linkable.paramValue, {
        replaceHistory,
      });
    }
  }

  if (morphPromise) await morphPromise;
};

const closeModal = (
  id: string,
  { animate = true, replaceHistory = true, updateUrl = true } = {},
): void => {
  const dialog = getDialog(id);
  if (dialog.open) {
    const card = sessionCardForModal(id);
    const animated = animate && !closesWithoutTransition();
    if (animated && canMorph(card)) {
      morphTransition({
        before: () => {
          setContainerMorph(dialog, true);
        },
        settle: () => {
          setContainerMorph(card, false);
        },
        swap: () => {
          dialog.close();
          setContainerMorph(dialog, false);
          releaseSessionCard(id);
          setContainerMorph(card, true);
        },
      });
    } else {
      if (animated) dismissDialog(dialog);
      else dialog.close();
      releaseSessionCard(id);
    }
  }

  if (updateUrl) {
    const linkable = getLinkableByModalId(id);
    if (!linkable) return;

    const current = new URLSearchParams(globalThis.location.search).get(linkable.paramName);
    if (current === linkable.paramValue) {
      updateQueryParam(linkable.paramName, null, { replaceHistory });
    }
  }
};

// Session detail modals are fetched on first open instead of pre-rendered
// (a big event has hundreds; rendering them all bloats the page and chokes the
// debug toolbar). The container carries a reverse()d URL with a `0` id
// placeholder; injected dialogs are cached in the DOM so reopen + morph-close
// stay instant.
declare const htmx: { process(el: Element): void };

// Each lazy source maps a dialog-id prefix to the container carrying a reverse()d
// URL template (a `0` id placeholder) and how to swap the pk in. Add an entry to
// lazy-fetch a new modal family; the rest of the machinery is prefix-agnostic.
interface LazyModalSource {
  prefix: string;
  urlAttr: string;
  urlFor: (template: string, pk: string) => string;
}

const LAZY_MODAL_SOURCES: LazyModalSource[] = [
  {
    prefix: "session-",
    urlAttr: "data-session-modal-url",
    urlFor: (template, pk) => template.replace(/\/session\/0\//, `/session/${pk}/`),
  },
  {
    prefix: "notification-modal-",
    urlAttr: "data-notification-modal-url",
    urlFor: (template, pk) => template.replace(/\/notifications\/0\//, `/notifications/${pk}/`),
  },
];

const inflightModals = new Map<string, Promise<boolean>>();

const sourceForId = (id: string): LazyModalSource | null =>
  LAZY_MODAL_SOURCES.find((source) => id.startsWith(source.prefix)) ?? null;

const lazyModalContainer = (source: LazyModalSource): HTMLElement | null =>
  document.querySelector<HTMLElement>(`[${source.urlAttr}]`);

const lazyModalUrl = (source: LazyModalSource, id: string): string | null => {
  const template = lazyModalContainer(source)?.getAttribute(source.urlAttr);
  return template ? source.urlFor(template, id.slice(source.prefix.length)) : null;
};

const numberWaitingPositions = (root: ParentNode): void => {
  for (const pane of root.querySelectorAll<HTMLElement>(".tab-panel")) {
    let position = 1;
    for (const badge of pane.querySelectorAll<HTMLElement>(
      "[data-waiting-list-row] [data-waiting-position]",
    )) {
      const n = position++;
      badge.textContent = String(n);
      const label = badge.dataset.positionLabel;
      if (label) badge.setAttribute("aria-label", `${label} ${n}`);
    }
  }
};

// Tabs, click-outside and Escape are delegated on document, so injected modals
// get those free. The close (×) button, waiting-list numbering and HTMX binding
// are per-element and must be wired on the fetched fragment.
const wireCloseButtons = (root: ParentNode): void => {
  for (const trigger of root.querySelectorAll("[data-modal-close]")) {
    trigger.addEventListener("touchend", closeFromTrigger, { capture: true });
    trigger.addEventListener("click", closeFromTrigger, { capture: true });
  }
};

const wireInjectedModal = (dialog: HTMLElement): void => {
  wireCloseButtons(dialog);
  numberWaitingPositions(dialog);
  htmx.process(dialog);
};

const fetchModal = async (id: string, url: string, source: LazyModalSource): Promise<boolean> => {
  const response = await fetch(url, {
    headers: { "X-Requested-With": "fetch" },
    signal: AbortSignal.timeout(10_000),
  });
  if (!response.ok) throw new Error(`modal ${id}: HTTP ${response.status}`);
  const html = await response.text();
  const template = document.createElement("template");
  template.innerHTML = html.trim();
  const dialog = template.content.firstElementChild;
  if (!(dialog instanceof HTMLElement) || dialog.id !== id) {
    throw new Error(`modal ${id}: unexpected fragment`);
  }
  lazyModalContainer(source)?.append(dialog);
  wireInjectedModal(dialog);
  return true;
};

/** Ensure the dialog for `id` is in the DOM, fetching it on first use. */
const ensureModalLoaded = async (id: string): Promise<boolean> => {
  if (document.getElementById(id)) return true;
  const source = sourceForId(id);
  if (!source) return false;
  const url = lazyModalUrl(source, id);
  if (!url) return false;

  let pending = inflightModals.get(id);
  if (!pending) {
    pending = fetchModal(id, url, source).catch((error: unknown) => {
      console.error(error);
      return false;
    });
    inflightModals.set(id, pending);
    void pending.finally(() => inflightModals.delete(id));
  }
  return pending;
};

const isLazyModal = (id: string): boolean => {
  const source = sourceForId(id);
  return source !== null && lazyModalContainer(source) !== null;
};

const syncModalsFromUrl = (): void => {
  if (openingModals.size > 0) return;

  const searchParams = new URLSearchParams(globalThis.location.search);

  for (const dialog of document.querySelectorAll("dialog.modal[open]")) {
    closeModal(dialog.id, { animate: false, updateUrl: false });
  }

  for (const link of document.querySelectorAll("a[href][aria-controls]")) {
    const href = link.getAttribute("href");
    const modalId = link.getAttribute("aria-controls");
    if (!href || !modalId) continue;

    const target = document.getElementById(modalId);
    if (
      !isLazyModal(modalId) &&
      !(target instanceof HTMLDialogElement && target.classList.contains("modal"))
    )
      continue;

    const hrefUrl = new URL(href, globalThis.location.href);
    for (const [paramName, paramValue] of hrefUrl.searchParams) {
      if (searchParams.get(paramName) === paramValue) {
        void ensureModalLoaded(modalId).then((ok) => {
          if (ok) void openModal(modalId, { animate: false, updateUrl: false });
        });
        return;
      }
    }
  }
};

document.addEventListener(
  "cancel",
  (event) => {
    const { target } = event;
    if (!(target instanceof HTMLDialogElement) || !target.classList.contains("modal")) return;

    event.preventDefault();
    closeModal(target.id);
  },
  true,
);

// NOTE: a form that reopens its modal after a refusal posts to that modal's
// own `?param` URL, so its resubmit looks just like a trigger click. `formData`
// marks a POST; `sourceElement` is the only mark a GET form leaves.
const submitsForm = (e: NavigateEvent): boolean =>
  e.formData instanceof FormData ||
  e.sourceElement instanceof HTMLFormElement ||
  e.sourceElement instanceof HTMLButtonElement ||
  e.sourceElement instanceof HTMLInputElement;

if (navigation) {
  navigation.addEventListener("navigate", (e) => {
    if (e.navigationType !== "push") return;
    if (!e.canIntercept || e.hashChange) return;
    if (submitsForm(e)) return;
    const url = new URL(e.destination.url);
    if (url.origin !== location.origin || url.pathname !== location.pathname) return;
    const reloadLink = [
      ...document.querySelectorAll<HTMLAnchorElement>("a[data-modal-reload]"),
    ].some((link) => new URL(link.href, location.href).href === url.href);
    if (reloadLink) return;

    for (const link of document.querySelectorAll<HTMLAnchorElement>("a[href][aria-controls]")) {
      if (Object.hasOwn(link.dataset, "modalReload")) continue;
      const href = link.getAttribute("href");
      const modalId = link.getAttribute("aria-controls");
      if (!href || !modalId) continue;

      const hrefUrl = new URL(href, location.href);
      if (hrefUrl.pathname !== url.pathname) continue;

      const matches =
        hrefUrl.searchParams.size > 0 &&
        [...hrefUrl.searchParams].every(([k, v]) => url.searchParams.get(k) === v);
      if (!matches) continue;

      const target = document.getElementById(modalId);
      if (
        !isLazyModal(modalId) &&
        !(target instanceof HTMLDialogElement && target.classList.contains("modal"))
      )
        continue;

      // Read before the navigation commits: the handler below runs after,
      // when location already shows the bare trigger href.
      const carriedParams = new URLSearchParams(globalThis.location.search);
      e.intercept({
        focusReset: "manual",
        async handler() {
          restoreCarriedSearchParams(carriedParams);
          if (await ensureModalLoaded(modalId)) {
            await openModal(modalId, { updateUrl: false });
          } else {
            // Lazy fetch failed: fall back to a real navigation to the trigger
            // href. That page's deep-link path retries without re-intercepting,
            // so there is no loop.
            globalThis.location.assign(href);
          }
        },
        scroll: "manual",
      });
      return;
    }
  });
}

const stopModalCloseEvent = (event: Event): void => {
  event.preventDefault();
  event.stopPropagation();
  if ("stopImmediatePropagation" in event) {
    event.stopImmediatePropagation();
  }
};

const closeFromTrigger = (event: Event): void => {
  const eventTarget = event.target;
  if (!(eventTarget instanceof Element)) return;

  const closeTrigger = eventTarget.closest<HTMLElement>("[data-modal-close]");
  if (!closeTrigger) return;

  const id = closeTrigger.dataset.modalClose;
  if (!id) return;

  stopModalCloseEvent(event);
  closeModal(id);
};

const setupModalCloseTriggers = (): void => {
  wireCloseButtons(document);
};

document.addEventListener("click", (event) => {
  const eventTarget = event.target;
  if (!(eventTarget instanceof Element)) return;

  if (!(eventTarget instanceof HTMLDialogElement) || !eventTarget.classList.contains("modal"))
    return;

  const rect = eventTarget.getBoundingClientRect();
  const isInside =
    event.clientX >= rect.left &&
    event.clientX <= rect.right &&
    event.clientY >= rect.top &&
    event.clientY <= rect.bottom;

  if (!isInside) closeModal(eventTarget.id);
});

globalThis.addEventListener("popstate", syncModalsFromUrl);

syncModalsFromUrl();
setupModalCloseTriggers();

// A trigger whose href leaves the current path can't be URL-addressed: the
// notification rows sit in the navbar, so one notification would get as many
// `?notification=` URLs as there are pages carrying the bell. Those open where
// the reader already is and leave the address bar alone; their href stays the
// real server route, which is what a no-JS click follows.
const opensInPlace = (href: string): boolean =>
  new URL(href, location.href).pathname !== location.pathname;

const setupModalLinkHandlers = (): void => {
  for (const link of document.querySelectorAll<HTMLAnchorElement>("a[href][aria-controls]")) {
    if (Object.hasOwn(link.dataset, "modalReload")) continue;
    // Re-run after an htmx swap brings new cards in; the links that survived
    // it keep the one handler they already have.
    if (Object.hasOwn(link.dataset, "modalClickBound")) continue;
    const modalId = link.getAttribute("aria-controls");
    const href = link.getAttribute("href");
    if (!modalId || !href) continue;

    const target = document.getElementById(modalId);
    if (
      !isLazyModal(modalId) &&
      !(target instanceof HTMLDialogElement && target.classList.contains("modal"))
    )
      continue;

    // Same-path triggers are the Navigation API's job where it exists; without
    // it every trigger needs the click handler.
    const inPlace = opensInPlace(href);
    if (navigation && !inPlace) continue;

    link.dataset.modalClickBound = "";
    link.addEventListener("click", (e) => {
      e.preventDefault();
      void ensureModalLoaded(modalId).then((ok) => {
        if (ok) void openModal(modalId, { updateUrl: !inPlace });
        else globalThis.location.assign(href);
      });
    });
  }
};

setupModalLinkHandlers();
document.body.addEventListener("htmx:afterSwap", setupModalLinkHandlers);

export { closeModal, openModal };
